import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IAuditsState } from "./definitions";

const initialState: IAuditsState = {
  taskList: [],
  taskGroupList: [],
  taskGroupRatios: [],
  assessmentList: [],
  paymentList: [],
  selectedAudit: {},
  teamName: "",
  headerSelectedTeam: "", // the team user selected from header drop down
  selectedTask: {},
  selectedAssessment: {
    tax: "",
    penalty: "",
    interest: "",
    other: "",
    creatingPaymentFromAssessment: false,
    taxAreaBreakdowns: [],
    taxYearBreakdowns: [],
    taxYearAreaBreakdowns: [],
  },
};

export const auditsSlice = createSlice({
  name: "audits",
  initialState,
  reducers: {
    removeSelectedAudit: (state) => {
      state.selectedAudit = {};
    },
    getAuditById: (state, action) => {
      return {
        ...state,
        selectedAudit: action.payload,
      };
    },
    addAssessmentsToList: (state, action) => {
      return {
        ...state,
        assessmentList: [...state.assessmentList, ...action.payload],
      };
    },
    deleteSingleAssessmentFromList: (state, action) => {
      return {
        ...state,
        assessmentList: state.assessmentList.filter(
          (assessment) => assessment.assessmentId !== action.payload
        ),
      };
    },
    setAssessmentList: (state, action) => {
      return {
        ...state,
        assessmentList: [...action.payload],
      };
    },
    updateSingleAssessment: (state, action) => {
      return {
        ...state,
        assessmentList: state.assessmentList.map((assessment) => {
          if (assessment.assessmentId === action.payload.assessmentId) {
            return { ...assessment, ...action.payload };
          }
          return assessment;
        }),
      };
    },
    addPaymentsToList: (state, action) => {
      return {
        ...state,
        paymentList: [...state.paymentList, ...action.payload],
      };
    },
    deleteSinglePaymentFromList: (state, action) => {
      return {
        ...state,
        paymentList: state.paymentList.filter(
          (payment) => payment.paymentId !== action.payload
        ),
      };
    },
    setPaymentList: (state, action) => {
      return {
        ...state,
        paymentList: [...action.payload],
      };
    },
    updateSinglePayment: (state, action) => {
      return {
        ...state,
        paymentList: state.paymentList.map((payment) => {
          if (payment.paymentId === action.payload.paymentId) {
            return { ...payment, ...action.payload };
          }
          return payment;
        }),
      };
    },
    setTaskList: (state, action) => {
      state.taskList = action.payload.tasks;
    },
    addTasksToList: (state, action) => {
      return { ...state, taskList: [...state.taskList, ...action.payload] };
    },
    deleteSingleTaskFromList: (state, action) => {
      return {
        ...state,
        taskList: state.taskList.filter(
          (task) => task.taskId !== action.payload
        ),
      };
    },
    removeTasksFromList: (state, action) => {
      return {
        ...state,
        taskList: state.taskList.filter(
          (task) => task.parentTaskId !== action.payload
        ),
      };
    },
    updateSingleTask: (state, action) => {
      return {
        ...state,
        taskList: state.taskList.map((task) => {
          if (task.taskId === action.payload.taskId) {
            return { ...task, ...action.payload };
          }
          return task;
        }),
      };
    },
    teamNameUpdated: (state, action: PayloadAction<string>) => {
      state.teamName = action.payload;
    },
    headerSelectedTeamUpdated: (state, action: PayloadAction<string>) => {
      state.headerSelectedTeam = action.payload;
    },
    removeSelectedTask: (state) => {
      state.selectedTask = {};
    },
    getTaskById: (state, action) => {
      return {
        ...state,
        selectedTask: action.payload,
      };
    },
    setSelectedAssessment: (state, action) => {
      return {
        ...state,
        selectedAssessment: {
          tax: action.payload.tax || "",
          penalty: action.payload.penalty || "",
          interest: action.payload.interest || "",
          other: action.payload.other || "",
          creatingPaymentFromAssessment:
            action.payload.creatingPaymentFromAssessment || false,
          taxAreaBreakdowns: action.payload.taxAreaBreakdowns,
          taxYearBreakdowns: action.payload.taxYearBreakdowns,
          taxYearAreaBreakdowns: action.payload.taxYearAreaBreakdowns,
        },
      };
    },
    resetSelectedAssessment: (state) => {
      return {
        ...state,
        selectedAssessment: {
          tax: "",
          penalty: "",
          interest: "",
          other: "",
          creatingPaymentFromAssessment: false,
          taxAreaBreakdowns: [],
          taxYearBreakdowns: [],
          taxYearAreaBreakdowns: [],
        },
      };
    },
    // Task Group actions
    setTaskGroupList: (state, action) => {
      state.taskGroupList = action.payload.taskGroups;
    },
    addTaskGroupsToList: (state, action) => {
      state.taskGroupList = [...state.taskGroupList, ...action.payload];
    },
    updateSingleTaskGroup: (state, action) => {
      state.taskGroupList = state.taskGroupList.map((taskGroup) => {
        if (taskGroup.taskId === action.payload.taskId) {
          return { ...taskGroup, ...action.payload };
        }
        return taskGroup;
      });
    },
    deleteSingleTaskGroupFromList: (state, action) => {
      state.taskGroupList = state.taskGroupList.filter(
        (taskGroup) => taskGroup.taskId !== action.payload
      );
    },
  },
});

export const {
  removeSelectedAudit,
  getAuditById,
  teamNameUpdated,
  headerSelectedTeamUpdated,
  setTaskList,
  addTasksToList,
  removeTasksFromList,
  deleteSingleTaskFromList,
  updateSingleTask,
  getTaskById,
  removeSelectedTask,
  addAssessmentsToList,
  deleteSingleAssessmentFromList,
  setAssessmentList,
  updateSingleAssessment,
  addPaymentsToList,
  deleteSinglePaymentFromList,
  setPaymentList,
  updateSinglePayment,
  setSelectedAssessment,
  resetSelectedAssessment,
  // Task Group actions
  setTaskGroupList,
  addTaskGroupsToList,
  updateSingleTaskGroup,
  deleteSingleTaskGroupFromList,
} = auditsSlice.actions;

export default auditsSlice.reducer;
